import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Injector,
  TemplateRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { FilterConnector } from '@core/models/entity/entity-selector-request.model';
import { ICONS } from '@core/services/icons/icons.service';

import { ButtonComponent } from '@shared/components/button/button.component';
import { DropdownService } from '@shared/components/dropdown-singleton/dropdown.service';

import { AddFilterDropdownComponent } from './add-filter-dropdown/add-filter-dropdown.component';
import { ComplexFilterBuilderComponent } from './complex-filter-builder/complex-filter-builder.component';
import { FilterOperationsService } from './filter-operations.service';
import { FilterPillComponent } from './filter-pill/filter-pill.component';
import { FilterPopupComponent } from './filter-popup/filter-popup.component';
import {
  AvailableFilterField,
  DateFilterCondition,
  DurationFilterCondition,
  FilterCondition,
  FilterConfiguration,
  FilterDataType,
  FilterGroup,
  FilterOperation,
  LabelFilterCondition,
  NumberFilterCondition,
  SelectFilterCondition,
  TagFilterCondition,
  TextFilterCondition,
  UidFilterCondition
} from './filter.models';

interface FilterPillDisplay {
  id: string;
  originalId?: string;
  label: string;
  type: FilterDataType;
  /** Translated operator, shown as the chip's middle segment. */
  operationLabel: string;
  /** Formatted value, empty when the operator carries the whole meaning. */
  value: string;
  condition: FilterCondition;
  element?: HTMLElement;
}

@Component({
  selector: 'app-filter-query-builder',
  standalone: true,
  imports: [
    CommonModule,
    FilterPillComponent,
    FilterPopupComponent,
    AddFilterDropdownComponent,
    ComplexFilterBuilderComponent,
    TranslatePipe,
    ButtonComponent
  ],
  templateUrl: './filter-query-builder.component.html',
  styleUrls: ['./filter-query-builder.component.css']
})
export class FilterQueryBuilderComponent {
  private translate = inject(TranslateService);
  private filterOpsService = inject(FilterOperationsService);
  private injector = inject(Injector);
  protected dropdownService = inject(DropdownService);

  // Template references
  protected filterPopupTemplate = viewChild<TemplateRef<unknown>>('filterPopupTemplate');
  protected addFilterTemplate = viewChild<TemplateRef<unknown>>('addFilterTemplate');
  protected scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

  // Inputs
  availableFields = input.required<AvailableFilterField[]>();
  initialConfiguration = input<FilterConfiguration | null>(null);
  openFilterForField = input<string | null>(null);

  // Outputs
  filterChange = output<FilterConfiguration>();

  // Signals
  protected filterConfiguration = signal<FilterConfiguration>(
    this.createEmptyConfiguration()
  );
  protected activeFilterCondition = signal<FilterCondition | null>(null);
  /** Condition whose parameter dropdown is open, so its pill can show the open state. */
  protected openPillId = signal<string | null>(null);
  protected showComplexFilterBuilder = signal(false);
  protected showLeftFade = signal<boolean>(false);
  protected showRightFade = signal<boolean>(false);
  protected newlyAddedFilterId = signal<string | null>(null);
  private lastProcessedFieldToOpen = signal<string | null>(null);

  protected readonly ICONS = ICONS;

  // Reactive language
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  // Computed
  protected filterPills = computed(() => {
    const config = this.filterConfiguration();
    const fields = this.availableFields();

    const pills: FilterPillDisplay[] = [];
    if (config.rootGroup) {
      this.collectFilterPills(config.rootGroup, pills, fields);
    }
    return pills;
  });

  protected hasComplexFilters = computed(() => {
    const config = this.filterConfiguration();
    return this.hasNestedGroups(config.rootGroup);
  });

  protected availableValuesForActiveCondition = computed(() => {
    const condition = this.activeFilterCondition();
    if (!condition) return [];

    if (
      condition.dataType === FilterDataType.LABELS ||
      condition.dataType === FilterDataType.TAGS ||
      condition.dataType === FilterDataType.SELECT ||
      condition.dataType === FilterDataType.MULTI_SELECT
    ) {
      const field = this.availableFields().find((f) =>
        f.dataType === FilterDataType.LABELS
          ? f.labelName === (condition as LabelFilterCondition).labelKey
          : (f.dbColumnName || f.field) === condition.field
      );
      return field?.availableValues || [];
    }

    return [];
  });

  constructor() {
    // A null configuration means the owner dropped every filter, so the bar has to
    // empty itself rather than keep chips describing filters nothing applies anymore.
    effect(() => {
      this.filterConfiguration.set(
        this.initialConfiguration() ?? this.createEmptyConfiguration()
      );
    });

    // Update scroll fade when filter pills change
    effect(() => {
      this.filterPills();

      const container = this.scrollContainer()?.nativeElement;
      if (container) {
        setTimeout(() => {
          this.updateScrollFade(container);
        }, 0);
      }
    });

    // Auto-open popup for newly added filter
    effect(() => {
      const newFilterId = this.newlyAddedFilterId();
      if (!newFilterId) return;

      // Use untracked() to prevent infinite loop when filterPills changes
      // This is critical for TAGS and LABELS which use AutoCompleteComponent
      const pills = untracked(() => this.filterPills());
      const pillIndex = pills.findIndex((p) => p.id === newFilterId);

      if (pillIndex !== -1) {
        // Use afterNextRender for reliable DOM timing
        afterNextRender(
          () => {
            const container = this.scrollContainer()?.nativeElement;
            if (!container) return;

            const pillElements = container.querySelectorAll('[data-pill-element]');
            const targetPillElement = pillElements[pillIndex] as HTMLElement;

            if (targetPillElement) {
              const pill = pills[pillIndex];
              this.openFilterPopup(pill, targetPillElement);
              // Clear signal to prevent re-triggering
              this.newlyAddedFilterId.set(null);
            }
          },
          { injector: this.injector }
        );
      }
    });

    // Auto-open popup for filter added via column header
    effect(() => {
      const fieldToOpen = this.openFilterForField();

      // Reset tracking when field is cleared
      if (!fieldToOpen) {
        this.lastProcessedFieldToOpen.set(null);
        return;
      }

      // Skip if already processed (prevents re-trigger during AutoComplete changes)
      if (this.lastProcessedFieldToOpen() === fieldToOpen) {
        return;
      }

      // Parse field name (handle dotted paths like "entity.field")
      const parsedFieldToOpen = fieldToOpen.split('.').slice(-1)[0];

      // Use untracked() to prevent infinite loop when filterPills changes
      // CRITICAL for TAGS/LABELS: AutoCompleteComponent triggers change detection
      // which recalculates filterPills, which would re-trigger this effect
      const pills = untracked(() => this.filterPills());
      const pillIndex = pills.findIndex(
        (p) => p.originalId === parsedFieldToOpen || p.id === parsedFieldToOpen
      );

      if (pillIndex !== -1) {
        // Mark as processed BEFORE afterNextRender to prevent race conditions
        this.lastProcessedFieldToOpen.set(fieldToOpen);

        // Use afterNextRender for reliable DOM timing
        afterNextRender(
          () => {
            const container = this.scrollContainer()?.nativeElement;
            if (!container) {
              console.warn('Scroll container not found for field:', fieldToOpen);
              return;
            }

            const pillElements = container.querySelectorAll('[data-pill-element]');
            const targetPillElement = pillElements[pillIndex] as HTMLElement;

            if (targetPillElement) {
              const pill = pills[pillIndex];
              this.openFilterPopup(pill, targetPillElement);
            } else {
              console.warn(
                `Target pill element not found at index ${pillIndex}. Total pills: ${pillElements.length}`
              );
            }
          },
          { injector: this.injector }
        );
      } else {
        console.warn(
          `No pill found matching field "${parsedFieldToOpen}". Available pills:`,
          pills.map((p) => ({ id: p.id, originalId: p.originalId }))
        );
      }
    });
  }

  protected onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    this.updateScrollFade(target);
  }

  private updateScrollFade(container: HTMLElement): void {
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Only show fades if there's actually overflow
    const hasOverflow = scrollWidth > clientWidth;

    if (!hasOverflow) {
      this.showLeftFade.set(false);
      this.showRightFade.set(false);
      return;
    }

    this.showLeftFade.set(scrollLeft > 10);
    this.showRightFade.set(scrollLeft + clientWidth < scrollWidth - 10);
  }

  protected openFilterPopup(pill: FilterPillDisplay, element: HTMLElement) {
    const template = this.filterPopupTemplate();
    if (!template) return;

    this.activeFilterCondition.set(pill.condition);
    this.openPillId.set(pill.condition.id);

    this.dropdownService.show(element, template, {
      position: 'bottom-start',
      middlewares: ['offset', 'flip', 'shift'],
      offset: 8,
      useDefaultStyles: false,
      closeOnClickOutside: true,
      closeOnEscape: true,
      onClose: () => this.openPillId.set(null)
    });
  }

  protected handleFilterUpdate(condition: FilterCondition) {
    const config = this.filterConfiguration();
    let updated: FilterConfiguration;

    const existing = this.findConditionInGroup(config.rootGroup, condition.id);

    if (existing) {
      updated = this.replaceConditionInGroup(config, condition);
    } else {
      updated = this.addConditionToRootGroup(config, condition);
    }

    this.filterConfiguration.set(updated);
    this.filterChange.emit(updated);
    this.dropdownService.close();
  }

  protected handleFilterRemove() {
    const condition = this.activeFilterCondition();
    if (!condition) return;

    const config = this.filterConfiguration();
    const updated = this.removeConditionFromGroup(config, condition.id);

    this.filterConfiguration.set(updated);
    this.filterChange.emit(updated);
    this.dropdownService.close();
  }

  protected handleFilterRemoveByPill(pill: FilterPillDisplay) {
    if (!pill.condition) return;

    const config = this.filterConfiguration();
    const updated = this.removeConditionFromGroup(config, pill.condition.id);

    this.filterConfiguration.set(updated);
    this.filterChange.emit(updated);

    // Close any popup that may have been opened for this pill so it does not
    // linger after the underlying filter is removed.
    this.dropdownService.close();
  }

  protected openAddFilterDropdown(event: ButtonComponent) {
    const template = this.addFilterTemplate();
    if (!template) return;

    const target = event.elementRef.nativeElement as HTMLElement;

    this.dropdownService.show(target, template, {
      position: 'bottom-start',
      middlewares: ['offset', 'flip', 'shift'],
      offset: 8,
      useDefaultStyles: false,
      closeOnClickOutside: true,
      closeOnEscape: true
    });
  }

  protected handleAddFilter(condition: FilterCondition) {
    const config = this.filterConfiguration();
    const updated = this.addConditionToRootGroup(config, condition);

    this.filterConfiguration.set(updated);
    // Don't emit filterChange yet - wait for user to configure and apply the filter
    this.dropdownService.close();

    // Store the newly added filter ID to auto-open its popup
    this.newlyAddedFilterId.set(condition.id);
  }

  protected openComplexFilterBuilder() {
    this.showComplexFilterBuilder.set(true);
  }

  protected closeComplexFilterBuilder() {
    this.showComplexFilterBuilder.set(false);
  }

  protected handleComplexFilterUpdate(config: FilterConfiguration) {
    this.filterConfiguration.set(config);
    this.filterChange.emit(config);
    this.closeComplexFilterBuilder();
  }

  protected trackPill(pill: FilterPillDisplay): string {
    return pill.id;
  }

  // Helper methods

  private createEmptyConfiguration(): FilterConfiguration {
    return {
      rootGroup: {
        id: this.generateId(),
        connector: FilterConnector.AND,
        conditions: []
      }
    };
  }

  private collectFilterPills(
    group: FilterGroup,
    pills: FilterPillDisplay[],
    fields: AvailableFilterField[]
  ) {
    for (const item of group.conditions) {
      if ('operation' in item) {
        const condition = item as FilterCondition;
        const field = fields.find((f) =>
          f.dataType === FilterDataType.LABELS
            ? f.labelName === (condition as LabelFilterCondition).labelKey
            : f.dbColumnName === condition.field || f.field === condition.field
        );

        if (field) {
          pills.push({
            id: condition.id,
            originalId: field.field,
            label: field.label,
            type: condition.dataType,
            operationLabel: this.filterOpsService.getOperationConfig(condition.operation)
              .label,
            value: this.getConditionDisplayValue(condition),
            condition
          });
        }
      } else if ('conditions' in item) {
        this.collectFilterPills(item as FilterGroup, pills, fields);
      }
    }
  }

  /**
   * The value part of a condition, without the operator: the chip renders the operator in
   * its own segment. Returns an empty string when the operator carries the whole meaning
   * (booleans, emptiness checks), so the chip can omit the value segment entirely.
   */
  private getConditionDisplayValue(condition: FilterCondition): string {
    if (
      condition.dataType === FilterDataType.BOOLEAN ||
      condition.operation === FilterOperation.IS_EMPTY ||
      condition.operation === FilterOperation.IS_NOT_EMPTY ||
      condition.operation === FilterOperation.IS_TRUE ||
      condition.operation === FilterOperation.IS_FALSE
    ) {
      return '';
    }

    const truncated = (values: string[]): string =>
      values.length > 3
        ? `${values.slice(0, 3).join(', ')} +${values.length - 3}`
        : values.join(', ');

    if (condition.dataType === FilterDataType.NUMBER) {
      const numCondition = condition as NumberFilterCondition;
      if (
        condition.operation === FilterOperation.BETWEEN &&
        numCondition.minValue &&
        numCondition.maxValue
      ) {
        return `${numCondition.minValue} - ${numCondition.maxValue}`;
      }
      if (!isNil(numCondition.value)) {
        return `${numCondition.value}`;
      }
    }

    if (condition.dataType === FilterDataType.DATE) {
      const dateCondition = condition as DateFilterCondition;
      if (
        condition.operation === FilterOperation.IS_RELATIVE &&
        dateCondition.relativeDate
      ) {
        return this.translate.instant(
          `filters.relative_dates.${dateCondition.relativeDate.toLowerCase()}`
        );
      }
      if (
        condition.operation === FilterOperation.BETWEEN &&
        dateCondition.minValue &&
        dateCondition.maxValue
      ) {
        return `${dateCondition.minValue} - ${dateCondition.maxValue}`;
      }
      if (dateCondition.value) {
        return dateCondition.value;
      }
    }

    if (condition.dataType === FilterDataType.DURATION) {
      const durationCondition = condition as DurationFilterCondition;
      if (
        condition.operation === FilterOperation.BETWEEN &&
        durationCondition.minValue &&
        durationCondition.maxValue
      ) {
        return `${durationCondition.minValue} - ${durationCondition.maxValue}`;
      }
      if (durationCondition.value) {
        return durationCondition.value;
      }
    }

    if (condition.dataType === FilterDataType.LABELS) {
      const labelCondition = condition as LabelFilterCondition;
      const labelKey = labelCondition.labelKey || '';

      if (
        condition.operation === FilterOperation.HAS_LABEL ||
        condition.operation === FilterOperation.NOT_HAS_LABEL
      ) {
        return labelKey;
      }
      if (
        (condition.operation === FilterOperation.LABEL_IN ||
          condition.operation === FilterOperation.LABEL_NOT_IN) &&
        labelCondition.labelValues?.length
      ) {
        return `${labelKey} [${labelCondition.labelValues.join(', ')}]`;
      }
      if (
        (condition.operation === FilterOperation.LABEL_EQUALS ||
          condition.operation === FilterOperation.LABEL_NOT_EQUALS) &&
        labelCondition.labelValue
      ) {
        return `${labelKey} = ${labelCondition.labelValue}`;
      }
    }

    if (condition.dataType === FilterDataType.TAGS) {
      const tagCondition = condition as TagFilterCondition;
      if (tagCondition.tagNames?.length) {
        return tagCondition.tagNames.join(', ');
      }
    }

    if (
      condition.dataType === FilterDataType.UID ||
      condition.dataType === FilterDataType.UID_LIST
    ) {
      const uidCondition = condition as UidFilterCondition;
      if (uidCondition.values?.length) {
        return truncated(uidCondition.values);
      }
      if (uidCondition.value) {
        return uidCondition.value;
      }
    }

    if (
      condition.dataType === FilterDataType.SELECT ||
      condition.dataType === FilterDataType.MULTI_SELECT
    ) {
      const selectCondition = condition as SelectFilterCondition;
      if (selectCondition.values?.length) {
        return truncated(selectCondition.values);
      }
    }

    if (
      condition.dataType === FilterDataType.TEXT ||
      condition.dataType === FilterDataType.MULTILINE_TEXT ||
      condition.dataType === FilterDataType.LINK
    ) {
      const textCondition = condition as TextFilterCondition;
      if (textCondition.value) {
        return textCondition.value;
      }
    }

    return '';
  }

  private hasNestedGroups(group: FilterGroup): boolean {
    return group.conditions.some((item) => 'conditions' in item);
  }

  private findConditionInGroup(
    group: FilterGroup,
    conditionId: string
  ): FilterCondition | null {
    for (const item of group.conditions) {
      if ('operation' in item && (item as FilterCondition).id === conditionId) {
        return item as FilterCondition;
      } else if ('conditions' in item) {
        const found = this.findConditionInGroup(item as FilterGroup, conditionId);
        if (found) return found;
      }
    }
    return null;
  }

  private addConditionToRootGroup(
    config: FilterConfiguration,
    condition: FilterCondition
  ): FilterConfiguration {
    return {
      ...config,
      rootGroup: {
        ...config.rootGroup,
        conditions: [...config.rootGroup.conditions, condition]
      }
    };
  }

  private replaceConditionInGroup(
    config: FilterConfiguration,
    condition: FilterCondition
  ): FilterConfiguration {
    const updateGroup = (group: FilterGroup): FilterGroup => {
      return {
        ...group,
        conditions: group.conditions.map((item) => {
          if ('operation' in item && (item as FilterCondition).id === condition.id) {
            return condition;
          } else if ('conditions' in item) {
            return updateGroup(item as FilterGroup);
          }
          return item;
        })
      };
    };

    return {
      ...config,
      rootGroup: updateGroup(config.rootGroup)
    };
  }

  private removeConditionFromGroup(
    config: FilterConfiguration,
    conditionId: string
  ): FilterConfiguration {
    const removeFromGroup = (group: FilterGroup): FilterGroup => {
      return {
        ...group,
        conditions: group.conditions
          .filter((item) => {
            if ('operation' in item) {
              return (item as FilterCondition).id !== conditionId;
            }
            return true;
          })
          .map((item) => {
            if ('conditions' in item) {
              return removeFromGroup(item as FilterGroup);
            }
            return item;
          })
      };
    };

    return {
      ...config,
      rootGroup: removeFromGroup(config.rootGroup)
    };
  }

  private generateId(): string {
    return `filter-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
